import { useMemo } from "react";
import clsx from "clsx";
import { FiChevronLeft, FiChevronRight } from "react-icons/fi";
import { ThreeDots } from "react-loader-spinner";

import NoRecordsAvailable from "../../../../../../components/NoRecordsAvailable";
import type {
  ApprovalEmployeeRow,
  ApprovalStatusFilter,
  ApproverGroup,
} from "../../types.ts";
import { ApproverGroupSection } from "./approver-group-section.tsx";
import type { ApprovalDashboardFilters } from "../../types.ts";

import styles from "./approval-dashboard-table.module.css";

export interface ApprovalDashboardTableProps {
  groups: ApproverGroup[];
  statusFilter: ApprovalStatusFilter;
  loading: boolean;
  page: number;
  size: number;
  /** 0-based page index returned by the API (used for serial numbers). */
  pageNumber: number;
  /** Page size returned by the API (used for serial numbers). */
  pageSize: number;
  totalElements: number;
  isLastPage: boolean;
  isFirstPage: boolean;
  onPageChange: (page: number) => void;
  onPageSizeChange: (size: number) => void;
  onView?: (row: ApprovalEmployeeRow) => void;
  filters: ApprovalDashboardFilters;
}

interface CountBadgeConfig {
  key: "totalPending" | "totalApproved" | "totalRejected";
  label: string;
  variant: "warning" | "success" | "danger";
}

const ALL_BADGES: CountBadgeConfig[] = [
  { key: "totalPending", label: "Pending", variant: "warning" },
  { key: "totalApproved", label: "Approved", variant: "success" },
  { key: "totalRejected", label: "Rejected", variant: "danger" },
];

function getBadgesForFilter(filter: ApprovalStatusFilter): CountBadgeConfig[] {
  if (filter === "ALL") return ALL_BADGES;
  if (filter === "PENDING") return [ALL_BADGES[0]];
  if (filter === "APPROVED") return [ALL_BADGES[1]];
  return [ALL_BADGES[2]];
}

function formatNumber(value: number): string {
  return value.toLocaleString();
}

export function ApprovalDashboardTable({
  groups,
  statusFilter,
  loading,
  page,
  size,
  pageNumber,
  pageSize,
  totalElements,
  isLastPage,
  isFirstPage,
  onPageChange,
  onPageSizeChange,
  onView,
  filters

  
}: ApprovalDashboardTableProps) {
  const badges = useMemo(() => getBadgesForFilter(statusFilter), [statusFilter]);
  const showStatusColumn = statusFilter === "ALL";
  const showOverdueColumn = statusFilter === "PENDING";
  const showActionColumn = typeof onView === "function";
  const rangeStart =
    totalElements > 0 && groups.length > 0 ? pageNumber * pageSize + 1 : 0;
  const rangeEnd = pageNumber * pageSize + groups.length;
  const canPrev = !isFirstPage;
  const canNext = !isLastPage;

  const totalColumns =
    1 /* # */ +
    1 /* Employee */ +
    1 /* Project */ +
    1 /* Date */ +
    (showStatusColumn ? 1 : 0) +
    (showOverdueColumn ? 1 : 0) +
    (showActionColumn ? 1 : 0);

  if (loading) {
    return (
      <div className={styles.loadingState}>
        <ThreeDots
          height="80"
          width="80"
          color="var(--color-accent-500)"
          visible
        />
      </div>
    );
  }

  if (groups.length === 0) {
    return <NoRecordsAvailable />;
  }

  return (
    <div className={styles.container}>
      <div className={styles.tableWrapper}>
        <table className={styles.table}>
          <thead className={styles.thead}>
            <tr>
              <th className={clsx(styles.th, styles.serialCell)} scope="col">
                #
              </th>
              <th className={styles.th} scope="col">
                Employee Name
              </th>
              <th className={styles.th} scope="col">
                Project
              </th>
              <th className={styles.th} scope="col">
                Timesheet Date
              </th>
              {showStatusColumn ? (
                <th className={styles.th} scope="col">
                  Status
                </th>
              ) : null}
              {showOverdueColumn ? (
                <th className={clsx(styles.th, styles.numberCell)} scope="col">
                  Overdue Days
                </th>
              ) : null}
              {showActionColumn ? (
                <th className={clsx(styles.th, styles.actionCell)} scope="col">
                  Action
                </th>
              ) : null}
            </tr>
          </thead>

          <tbody>
            {groups.map((group, groupIdx) => (
              <ApproverGroupSection
                key={`${group.approverId}-${statusFilter}`}
                group={group}
                groupIdx={groupIdx}
                approverSerial={pageNumber * pageSize + groupIdx + 1}
                statusFilter={statusFilter}
                badges={badges}
                showStatusColumn={showStatusColumn}
                showOverdueColumn={showOverdueColumn}
                showActionColumn={showActionColumn}
                totalColumns={totalColumns}
                onView={onView}
                 filters={filters}
              />
            ))}
          </tbody>
        </table>
      </div>

      <div className={styles.outerPagination}>
        <label className={styles.pageSize}>
          <span>Rows per page</span>
          <select
            className={styles.pageSizeSelect}
            value={String(size)}
            onChange={(e) => onPageSizeChange(Number(e.target.value))}
            aria-label="Rows per page"
          >
            {[5, 10, 20, 50].map((n) => (
              <option key={n} value={n}>
                {n}
              </option>
            ))}
          </select>
        </label>

        <div className={styles.innerPagination}>
          <span className={styles.innerPaginationRange}>
            {rangeStart > 0
              ? `${formatNumber(rangeStart)}\u2013${formatNumber(rangeEnd)} of ${formatNumber(totalElements)}`
              : `0 of ${formatNumber(totalElements)}`}
          </span>
          <div className={styles.innerPaginationNav}>
            <button
              type="button"
              className={styles.innerPaginationButton}
              disabled={!canPrev}
              aria-label="Previous page"
              onClick={() => onPageChange(page - 1)}
            >
              <FiChevronLeft size={18} aria-hidden />
            </button>
            <button
              type="button"
              className={styles.innerPaginationButton}
              disabled={!canNext}
              aria-label="Next page"
              onClick={() => onPageChange(page + 1)}
            >
              <FiChevronRight size={18} aria-hidden />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default ApprovalDashboardTable;
